import React, { useMemo } from 'react';
import { Crown, Trophy, Play, CheckCircle, Clock, ArrowRight } from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * BracketTreeView
 * Renders the knockout stage of a tournament as a horizontal bracket tree
 * with CSS connector lines between rounds. Bye slots are rendered with a
 * dashed border to visually distinguish them.
 *
 * Props:
 *  - tournament: full tournament object (must include knockout.rounds[])
 *  - matches:    array of tournament_matches (with team1_id, team2_id, scores, winner_id, status)
 *  - getTeamName: (team_id) => string
 *  - onScoreMatch?: (match) => void   organizer-only: open score dialog
 *  - onStartMatch?: (match) => void   organizer-only: open start-match dialog
 *  - isOrganizer?: boolean
 */
export default function BracketTreeView({
  tournament,
  matches,
  getTeamName,
  onScoreMatch,
  onStartMatch,
  isOrganizer = false,
}) {
  const rounds = tournament?.knockout?.rounds || [];
  const matchById = useMemo(() => {
    const map = {};
    (matches || []).forEach(m => { map[m.match_id] = m; });
    return map;
  }, [matches]);

  if (!rounds.length) {
    return (
      <div className="rounded-lg border border-dashed py-12 text-center text-muted-foreground">
        Knockout stage has not started yet
      </div>
    );
  }

  // Build cells per round in slot order. New format uses `advancements[]` which
  // preserves the seeded bracket order. Legacy rounds (no advancements) fall back
  // to listing matches first, then byes — visually correct but not slot-aligned.
  const roundsWithCells = rounds.map(round => {
    let cells;
    if (Array.isArray(round.advancements) && round.advancements.length > 0) {
      cells = round.advancements.map(adv => {
        if (adv.type === 'bye') return { type: 'bye', team_id: adv.team_id };
        if (adv.type === 'match') return { type: 'match', match_id: adv.match_id };
        return { type: 'empty' };
      });
    } else {
      cells = [
        ...(round.match_ids || []).map(id => ({ type: 'match', match_id: id })),
        ...(round.byes || []).map(tid => ({ type: 'bye', team_id: tid })),
      ];
    }
    return { ...round, cells };
  });

  // Champion (if final is complete)
  const finalRound = roundsWithCells[roundsWithCells.length - 1];
  const finalMatch = finalRound?.cells?.find(c => c.type === 'match');
  const finalMatchDoc = finalMatch ? matchById[finalMatch.match_id] : null;
  const championId = finalMatchDoc?.status === 'completed' ? finalMatchDoc?.winner_id : null;

  return (
    <div className="bracket-tree-wrapper" data-testid="bracket-tree-view">
      <div className="bracket-tree">
        {roundsWithCells.map((round, rIdx) => {
          const isFinalRound = rIdx === roundsWithCells.length - 1;
          // Round name is stable per round (Quarter Final / Semi Final / Final / Round of N),
          // so use it as the key. Fall back to rIdx only if name is somehow missing.
          const roundKey = round.round || `round-${rIdx}`;
          return (
            <div
              key={roundKey}
              className="bracket-round"
              data-is-final={isFinalRound ? 'true' : 'false'}
              data-testid={`bracket-round-${rIdx}`}
            >
              <div className="bracket-round-label">
                <Crown className="h-3.5 w-3.5 inline-block mr-1 text-accent align-[-2px]" />
                {round.round}
              </div>
              <div className="bracket-round-cells" style={{ '--cell-count': round.cells.length }}>
                {round.cells.map((cell, cIdx) => {
                  const pairPos = cIdx % 2 === 0 ? 'top' : 'bottom';
                  const showConnectors = !isFinalRound;

                  if (cell.type === 'bye') {
                    return (
                      <div
                        key={`bye-${cell.team_id}`}
                        className="bracket-cell"
                        data-pair-pos={pairPos}
                        data-cell-type="bye"
                      >
                        <ByeCard teamName={getTeamName(cell.team_id)} />
                        {showConnectors && pairPos === 'top' && <Connectors />}
                      </div>
                    );
                  }

                  if (cell.type === 'empty') {
                    return (
                      <div key={`empty-${cIdx}`} className="bracket-cell" data-pair-pos={pairPos} data-cell-type="empty">
                        <div className="bracket-card bracket-empty" aria-hidden>—</div>
                        {showConnectors && pairPos === 'top' && <Connectors />}
                      </div>
                    );
                  }

                  const m = matchById[cell.match_id];
                  return (
                    <div
                      key={cell.match_id || `m-${cIdx}`}
                      className="bracket-cell"
                      data-pair-pos={pairPos}
                      data-cell-type="match"
                    >
                      <MatchCard
                        match={m}
                        getTeamName={getTeamName}
                        isOrganizer={isOrganizer}
                        onScoreMatch={onScoreMatch}
                        onStartMatch={onStartMatch}
                      />
                      {showConnectors && pairPos === 'top' && <Connectors />}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Champion column */}
        <div className="bracket-round bracket-round-champion" data-is-final="true">
          <div className="bracket-round-label">
            <Trophy className="h-3.5 w-3.5 inline-block mr-1 text-accent align-[-2px]" />
            Champion
          </div>
          <div className="bracket-round-cells">
            <div className="bracket-cell" data-pair-pos="top">
              <div className={cn(
                'bracket-card bracket-champion',
                championId && 'bracket-champion-decided'
              )}>
                {championId ? (
                  <>
                    <Trophy className="h-6 w-6 text-accent mx-auto mb-1" />
                    <div className="text-[10px] tracking-widest text-muted-foreground">WINNER</div>
                    <div className="font-heading font-bold text-sm truncate" title={getTeamName(championId)}>
                      {getTeamName(championId)}
                    </div>
                  </>
                ) : (
                  <div className="text-xs text-muted-foreground italic">TBD</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────── sub-components ───────────────────────── */

function MatchCard({ match, getTeamName, isOrganizer, onScoreMatch, onStartMatch }) {
  if (!match) {
    return (
      <div className="bracket-card bracket-match" data-status="pending">
        <div className="text-xs text-muted-foreground">Awaiting setup</div>
      </div>
    );
  }
  const t1Name = getTeamName(match.team1_id);
  const t2Name = getTeamName(match.team2_id);
  const isCompleted = match.status === 'completed';
  const isLive = match.status === 'in_progress';
  const t1Won = isCompleted && match.winner_id === match.team1_id;
  const t2Won = isCompleted && match.winner_id === match.team2_id;

  // Total games won per team across the scores array
  const t1Games = (match.scores || []).filter(s => s.winner === 'team1' || s.winner === match.team1_id).length;
  const t2Games = (match.scores || []).filter(s => s.winner === 'team2' || s.winner === match.team2_id).length;

  const handleClick = () => {
    if (!isOrganizer) return;
    if (isCompleted) return;
    if (match.status === 'pending' && onStartMatch) return onStartMatch(match);
    if (onScoreMatch) return onScoreMatch(match);
  };

  return (
    <div
      className={cn(
        'bracket-card bracket-match',
        isOrganizer && !isCompleted && 'cursor-pointer hover:border-accent',
      )}
      data-status={match.status}
      onClick={handleClick}
      role={isOrganizer && !isCompleted ? 'button' : undefined}
      title={isOrganizer && !isCompleted ? 'Tap to manage match' : undefined}
    >
      <TeamRow name={t1Name} games={t1Games} isWinner={t1Won} />
      <div className="bracket-divider" />
      <TeamRow name={t2Name} games={t2Games} isWinner={t2Won} />
      <div className="bracket-status">
        {isCompleted && <CheckCircle className="h-3 w-3 text-accent" />}
        {isLive && <Play className="h-3 w-3 text-destructive animate-pulse" />}
        {match.status === 'pending' && <Clock className="h-3 w-3 text-muted-foreground" />}
        <span className="text-[10px] tracking-widest uppercase text-muted-foreground">
          {isCompleted ? 'Final' : isLive ? 'Live' : 'Pending'}
        </span>
      </div>
    </div>
  );
}

function TeamRow({ name, games, isWinner }) {
  return (
    <div className={cn('bracket-team', isWinner && 'bracket-team-winner')}>
      <span className="bracket-team-name" title={name}>{name || '—'}</span>
      <span className="bracket-team-score">
        {games > 0 ? games : ''}
      </span>
    </div>
  );
}

function ByeCard({ teamName }) {
  return (
    <div className="bracket-card bracket-bye" data-testid="bracket-bye-cell">
      <div className="bracket-bye-label">
        <ArrowRight className="h-3 w-3 inline-block mr-1 align-[-1px]" />
        BYE — AUTO ADVANCE
      </div>
      <div className="bracket-team bracket-team-winner">
        <span className="bracket-team-name" title={teamName}>{teamName || '—'}</span>
      </div>
    </div>
  );
}

function Connectors() {
  // Drawn only on pair-top cells; uses CSS positioning to extend to pair-bottom
  // sibling and forward to the next round.
  return (
    <>
      <span className="bracket-connector-vertical" aria-hidden />
      <span className="bracket-connector-forward" aria-hidden />
    </>
  );
}
